/**
 * GenericCarbonStructuring — Transaction-Ready Project-Level Carbon Structuring
 * Route: /project/:id/carbon-structuring
 *
 * Upgrades: Pathway upgrade logic, PoA scale potential, aggregation with
 *           bankability signal, readiness indicator, Next Steps to Monetisation.
 */
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProjectWorkspace } from "@/hooks/useProjectWorkspace";
import { useOkoObaStructuringData } from "@/store/projectStore";
import { PageHeader, SectionCard } from "@/components/ds";
import {
  RefreshCw, CheckCircle2, XCircle, AlertTriangle, Info,
  ChevronDown, ChevronUp, Download, Shield, ArrowRight
} from "lucide-react";
import client from "@/api/client";

const fmtV = (v) => v >= 1_000_000 ? `$${(v/1e6).toFixed(1)}M` : v >= 100_000 ? `$${(v/1e6).toFixed(2)}M` : `$${Math.round(v/1000)}K`;
const fmtK = (v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : `${Math.round(v)}`;

// ── Bankability signal (derived — no hardcoding) ───────────────────────────────
function getBankabilitySignal(issLabel, addLabel, evPct) {
  if (issLabel === "Issuance Ready") return { label: "Financing-ready",    cls: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  if (issLabel === "Structuring Phase" && evPct >= 70)
                                      return { label: "Structuring-ready",  cls: "bg-blue-100 text-blue-700 border-blue-200" };
  if (issLabel === "Structuring Phase")return { label: "Pre-investment",    cls: "bg-amber-100 text-amber-700 border-amber-200" };
  return                                      { label: "Early stage",        cls: "bg-slate-100 text-slate-500 border-slate-200" };
}

// ── Scale potential (derived from activity type + total reduction) ─────────────
function getScalePotential(activityType, totalRedPerSite) {
  const ranges = {
    biogas_waste:           { sites: [10, 50], label: "similar waste-to-biogas facilities" },
    returnable_bottles:     { sites: [5, 20],  label: "similar packaging circularity sites" },
    solar_pv:               { sites: [5, 30],  label: "similar renewable energy sites" },
    biomass_boiler:         { sites: [5, 20],  label: "similar fuel-switch facilities" },
    heat_recovery:          { sites: [5, 15],  label: "similar energy efficiency projects" },
    logistics_optimization: { sites: [3, 10],  label: "similar distribution networks" },
  };
  const r = ranges[activityType] || { sites: [5, 20], label: "similar project sites" };
  const lowRed  = Math.round(totalRedPerSite * r.sites[0] / 1000);
  const highRed = Math.round(totalRedPerSite * r.sites[1] / 1000);
  return {
    siteRange: r.sites,
    siteLabel: r.label,
    redRange: [lowRed, highRed],
  };
}

// ── Pathway upgrade steps (dynamic) ───────────────────────────────────────────
function getUpgradeSteps(pathwayType, issLabel) {
  const done = (label) => {
    if (issLabel === "Issuance Ready") return true;
    if (issLabel === "Structuring Phase") return ["Methodology selection", "VVB engagement"].includes(label);
    return false;
  };
  if (pathwayType === "vcm") return [
    { text: "Methodology selection (AMS-III.D / VM0025)", met: done("Methodology selection") },
    { text: "Project Design Document (PDD) preparation",  met: issLabel !== "Early Stage" },
    { text: "MRV plan finalisation",                       met: false },
    { text: "VVB engagement for validation",               met: done("VVB engagement") },
    { text: "Registry registration & credit issuance",     met: issLabel === "Issuance Ready" },
  ];
  return [
    { text: "VVB technical validation",                          met: false },
    { text: "Methodology registration with supervisory body",    met: false },
    { text: "Host country Letter of Authorization (LoA)",        met: false },
    { text: "Corresponding adjustment confirmation (NDC)",       met: false },
    { text: "ITMO transfer registration",                        met: false },
  ];
}

// ── Next steps to monetisation (dynamic) ──────────────────────────────────────
function getNextSteps(issLabel, vcmStatus, art6Status, evPct, showPoA) {
  return [
    { text: "Complete MRV documentation and data submission",
      done: evPct >= 70, priority: "high" },
    { text: "Select and register methodology (AMS-III.D or VM0025)",
      done: vcmStatus === "recommended", priority: "high" },
    { text: "Engage accredited VVB for independent validation",
      done: issLabel === "Issuance Ready", priority: "high" },
    { text: showPoA ? "Establish PoA coordinating entity and CPA registration" : null,
      done: false, priority: "medium" },
    { text: art6Status !== "not_eligible" ? "Apply for host country LoA (NCCC) if pursuing Art.6" : null,
      done: false, priority: "medium" },
    { text: "Register project with carbon registry (Verra / Gold Standard)",
      done: false, priority: "medium" },
    { text: "Issue first carbon credits and connect to offtake buyer",
      done: false, priority: "low" },
  ].filter(s => s.text !== null);
}

// ── Pathway card ──────────────────────────────────────────────────────────────
function PathwayCard({ title, statusLabel, badge, reasoning, nextAction, requirements, dot, issLabel, pathwayType }) {
  const [open, setOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const isRec = badge === "HIGH" || badge === "Recommended";
  const borderCls = isRec ? "border-emerald-200" : "border-amber-200";
  const bgCls     = isRec ? "bg-emerald-50" : "bg-amber-50";
  const badgeCls  = isRec
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : "bg-amber-100 text-amber-700 border-amber-200";
  const met   = (requirements||[]).filter(r=>r.met).length;
  const total = (requirements||[]).length;
  const upgradeSteps = getUpgradeSteps(pathwayType, issLabel);
  const upgradeMetCount = upgradeSteps.filter(s=>s.met).length;

  return (
    <div className={`border rounded-xl overflow-hidden ${borderCls}`}>
      <button onClick={() => setOpen(v=>!v)} className={`w-full px-5 py-4 flex items-center justify-between text-left ${bgCls}`}>
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full inline-block flex-shrink-0" style={{background: dot}}/>
          <div>
            <p className="text-sm font-bold text-slate-900">{title}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{reasoning}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${badgeCls}`}>{statusLabel}</span>
          {total > 0 && <span className="text-[9px] text-slate-400">{met}/{total} req.</span>}
          {open ? <ChevronUp size={13} className="text-slate-400"/> : <ChevronDown size={13} className="text-slate-400"/>}
        </div>
      </button>

      {open && (
        <div className="px-5 pb-5 pt-3 bg-white border-t" style={{borderColor:'inherit'}}>
          {/* Existing requirements */}
          {total > 0 && (
            <div className="mb-4">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Eligibility Requirements</p>
              <div className="space-y-1">
                {(requirements||[]).map((r,i) => (
                  <div key={i} className="flex items-start gap-1.5 text-xs">
                    {r.met ? <CheckCircle2 size={11} className="text-emerald-500 flex-shrink-0 mt-0.5"/>
                           : <XCircle     size={11} className="text-red-400 flex-shrink-0 mt-0.5"/>}
                    <span className={r.met ? "text-slate-700" : "text-slate-400"}>{r.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pathway upgrade logic — top 2 unmet steps visible, full list expandable */}
          <div className="border border-slate-200 rounded-xl overflow-hidden mt-4">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold text-slate-700">Steps to issuance</p>
                <button
                  onClick={() => setShowUpgrade(v=>!v)}
                  className="text-[9px] font-semibold text-teal-600 hover:text-teal-800 flex items-center gap-1">
                  {showUpgrade ? "Collapse" : `View full pathway (${upgradeSteps.length} steps)`}
                  {showUpgrade ? <ChevronUp size={10}/> : <ChevronDown size={10}/>}
                </button>
              </div>
            </div>
            <div className="px-4 py-3 space-y-1.5">
              {/* Always show top 2 unmet steps */}
              {upgradeSteps.filter(s=>!s.met).slice(0,2).map((s,i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <AlertTriangle size={11} className="text-amber-500 flex-shrink-0"/>
                  <span className="text-slate-600 font-medium">{s.text}</span>
                  <span className="text-[9px] text-amber-600 font-bold ml-auto whitespace-nowrap">pending</span>
                </div>
              ))}
              {/* Full list when expanded */}
              {showUpgrade && (
                <div className="mt-2 pt-2 border-t border-slate-100 space-y-1.5">
                  {upgradeSteps.map((s,i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {s.met
                        ? <CheckCircle2 size={11} className="text-emerald-500 flex-shrink-0"/>
                        : <div className="w-3 h-3 rounded-full border-2 border-slate-300 flex-shrink-0"/>}
                      <span className={s.met ? "text-slate-700" : "text-slate-400"}>{s.text}</span>
                      {s.met && <span className="text-[9px] text-emerald-600 font-bold ml-auto">complete</span>}
                    </div>
                  ))}
                  <p className="text-[9px] text-slate-400 mt-1">
                    {upgradeMetCount}/{upgradeSteps.length} complete
                  </p>
                </div>
              )}
            </div>
          </div>

          {nextAction && (
            <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[10px] text-slate-600">
              <span className="font-bold text-slate-500 uppercase">Immediate next: </span>{nextAction}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── LoA Pack download ─────────────────────────────────────────────────────────
function ProjectLoAButton({ projectId }) {
  const [loading, setLoading] = useState(false);
  const handleDownload = async () => {
    setLoading(true);
    try {
      const resp = await client.get(`/workspace/project/${projectId}/pdd`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], {type:"application/pdf"}));
      const a = document.createElement("a"); a.href = url;
      a.download = `OkoOba_StructuringPack_${projectId}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { alert("Structuring pack not available. PDD generation in progress."); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={handleDownload} disabled={loading}
      className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold rounded-lg disabled:opacity-50">
      {loading ? <RefreshCw size={10} className="animate-spin"/> : <Download size={10}/>}
      {loading ? "Generating..." : "Download Structuring Pack"}
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function GenericCarbonStructuring() {
  const { id } = useParams();
  const navigate = useNavigate();

  // SSOT data from project workspace hook
  const {
    loading, journey, reduction, carbonPathway,
    evidenceCoverage, org, sector,
  } = useProjectWorkspace(id);

  // Shared store enrichment (zero duplication, zero NB leakage)
  const {
    activities,
    cpaGroups,
    showCPA,
    showPoA,
    aggregation,
    poaStatus,
  } = useOkoObaStructuringData({
    journey,
    reduction,
    carbonPath: carbonPathway,
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500"/>
    </div>
  );

  const pathways     = carbonPathway?.pathways || {};
  const vcmPath      = pathways.vcm || {};
  const art6Path     = pathways.article6 || {};
  const poaPath      = pathways.poa || {};
  const totalRed     = Number(reduction?.total_tco2e_yr || 0);
  const evPct        = evidenceCoverage?.coverage_pct || 0;
  const projectName  = journey?.name || org?.name || "Oko-Oba";

  const vcmPct  = aggregation.vcmRed > 0 && totalRed > 0 ? Math.round(aggregation.vcmRed/totalRed*100) : 0;
  const art6Pct = aggregation.art6Red > 0 && totalRed > 0 ? Math.round(aggregation.art6Red/totalRed*100) : 0;

  // Issuance readiness (per activity — from store)
  const mainActivity  = activities[0] || {};
  const issReady      = mainActivity.issuance_readiness || { label: "Early Stage", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  const addSig        = mainActivity.additionality || { label: "Investment barrier", cls: "text-emerald-700", sub: "Capital cost limits unaided viability" };

  // Derived transaction-readiness signals
  const bankability  = getBankabilitySignal(issReady.label, addSig.label, evPct);
  const scalePot     = getScalePotential(mainActivity.opp_type || "biogas_waste", totalRed);
  // useMemo replaced with direct computation — simpler, avoids conditional hook issue
  const nextSteps    = getNextSteps(issReady.label, vcmPath.status, art6Path.status, evPct, showPoA);

  // Pathway status labels (no percentages — qualitative representation)
  const vcmStatusLabel  = vcmPath.status === "recommended" ? "Primary — Recommended"     : "Conditional";
  const art6StatusLabel = art6Path.status === "recommended" ? "Primary — Recommended"    :
                          art6Path.status === "not_eligible" ? "Not Eligible"             :
                          "Conditional — Requires host country authorization (LoA) and corresponding adjustment";

  // Next critical action — highest priority incomplete step
  const nextCriticalAction = nextSteps.find(s => !s.done && s.priority === "high")
    || nextSteps.find(s => !s.done && s.priority === "medium")
    || nextSteps.find(s => !s.done);

  return (
    <div className="p-6 lg:p-8 space-y-8 max-w-5xl">

      {/* ── STRUCTURING STATUS SUMMARY BAR (top strip) ──────────────────── */}
      <div className="bg-slate-900 rounded-xl px-5 py-4">
        <p className="text-[9px] text-slate-400 uppercase tracking-widest font-bold mb-3">Structuring Status Summary</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            {
              label: "Project Status",
              value: issReady.label,
              sub: projectName,
              dot: issReady.label === "Issuance Ready" ? "#10b981" : issReady.label === "Structuring Phase" ? "#f59e0b" : "#94a3b8",
            },
            {
              label: "Primary Pathway",
              value: "VCM",
              sub: vcmPath.status === "recommended" ? "Recommended" : "Conditional",
              dot: "#059669",
            },
            {
              label: "Secondary Pathway",
              value: "Article 6",
              sub: "Conditional — requires LoA",
              dot: "#3b82f6",
            },
            {
              label: "Next Critical Action",
              value: nextCriticalAction ? nextCriticalAction.text.split(" (")[0].slice(0, 30) + "..." : "No pending actions",
              sub: nextCriticalAction ? `Priority: ${nextCriticalAction.priority}` : "",
              dot: nextCriticalAction?.priority === "high" ? "#dc2626" : "#f59e0b",
            },
          ].map(c => (
            <div key={c.label}>
              <p className="text-[9px] text-slate-500 uppercase tracking-widest font-semibold mb-1">{c.label}</p>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{background: c.dot}}/>
                <p className="text-xs font-bold text-white leading-tight">{c.value}</p>
              </div>
              {c.sub && <p className="text-[9px] text-slate-400 mt-0.5 ml-3">{c.sub}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div>
        <button onClick={() => navigate(`/project/${id}/workspace`)}
          className="text-[10px] text-slate-400 hover:text-slate-700 mb-3 flex items-center gap-1">
          ← {projectName}
        </button>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Project-Level Structuring</p>
        <PageHeader title="Carbon Structuring" />
        <p className="text-xs text-slate-400 mt-1">
          VCM / Article 6 Ready · {totalRed > 0 ? `${fmtK(totalRed)} tCO₂e/yr reduction potential` : "Calculating…"}
        </p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${issReady.cls}`}>
            {issReady.label}
          </span>
          <span className="text-[10px] text-slate-400 border border-slate-200 rounded-full px-2 py-0.5">
            Evidence {evPct}% · {activities.length} activity
          </span>
          {vcmPath.status === "recommended" && (
            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              VCM Recommended
            </span>
          )}
        </div>
      </div>

      {/* ── STRUCTURING OVERVIEW ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Carbon Pathways",   value: "VCM + Art.6",      sub: vcmPath.status === "recommended" ? "VCM Recommended" : "Conditional",    color: "border-blue-400" },
          { label: "CPA Groups",        value: cpaGroups.length > 0 ? String(cpaGroups.length) : "1", sub: showCPA ? "Multiple — section shown" : "Single activity type",  color: "border-amber-400" },
          { label: "PoA Viability",     value: poaStatus === "recommended" ? "Viable" : "Conditional", sub: poaPath.reasoning?.slice(0,45) || "Expandable to multiple sites", color: "border-purple-400" },
          { label: "Project Scale",     value: fmtK(totalRed),     sub: "tCO₂e/yr · " + (totalRed >= 5000 ? "Art.6 threshold met" : "VCM scale"),    color: "border-emerald-400" },
        ].map(c => (
          <div key={c.label} className={`bg-white border border-slate-200 border-t-2 ${c.color} rounded-xl p-4`}>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-2">{c.label}</p>
            <p className="text-lg font-bold text-slate-900 leading-tight">{c.value}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* ── SECTION 1: CARBON PATHWAYS (mandatory) ──────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 bg-blue-500 rounded-full flex-shrink-0"/>
          <p className="text-sm font-bold text-slate-900">Carbon Pathways</p>
          <span className="text-[9px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 ml-1">Step 1 of {showCPA ? 4 : (showPoA ? 3 : 2)}</span>
        </div>
        <p className="text-xs text-slate-500 mb-4 max-w-2xl">
          The available carbon market routes for {projectName}. Each pathway has distinct requirements,
          timelines, and integrity standards. Assessments are indicative only — not a carbon eligibility determination.
        </p>

        <div className="space-y-3">
          {/* VCM */}
          <PathwayCard
            title="Voluntary Carbon Market (VCM)"
            statusLabel={vcmStatusLabel}
            badge={vcmPath.badge || "MED"}
            dot="#059669"
            reasoning={vcmPath.reasoning || ""}
            nextAction={vcmPath.next_action}
            requirements={vcmPath.requirements || []}
            issLabel={issReady.label}
            pathwayType="vcm"
          />
          {/* Article 6 */}
          <PathwayCard
            title="Article 6 — ITMO Pathway"
            statusLabel={art6StatusLabel}
            badge={art6Path.badge || "MED"}
            dot="#3b82f6"
            reasoning={art6Path.reasoning || "Requires host country authorization (LoA) and corresponding adjustment before any ITMO can be issued."}
            nextAction={art6Path.next_action}
            requirements={art6Path.requirements || []}
            issLabel={issReady.label}
            pathwayType="article6"
          />
        </div>

        {/* Per-activity additionality + issuance readiness */}
        {activities.length > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Activity Structuring Detail</p>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  {["Activity","Additionality Signal (Preliminary)","Pathway","Issuance Readiness","VCM","Art.6"].map(h=>(
                    <th key={h} className="text-left text-[9px] text-slate-400 pb-2 px-4 pt-3 font-semibold uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activities.map(a => (
                  <tr key={a.opp_id} className="border-b border-slate-50 last:border-0">
                    <td className="py-3 px-4 font-semibold text-slate-800 max-w-[180px]">{a.name}</td>
                    <td className="py-3 px-4">
                      <p className={`text-[10px] font-semibold leading-tight ${a.additionality?.cls}`}>{a.additionality?.label}</p>
                      <p className="text-[9px] text-slate-400">{a.additionality?.sub}</p>
                    </td>
                    <td className="py-3 px-4 text-[10px] text-slate-500">{a.pathway_label}</td>
                    <td className="py-3 px-4">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${a.issuance_readiness?.cls}`}>
                        {a.issuance_readiness?.label}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {a.vcm_eligible ? <CheckCircle2 size={13} className="text-emerald-500"/> : <XCircle size={13} className="text-slate-300"/>}
                    </td>
                    <td className="py-3 px-4">
                      {a.art6_eligible ? <CheckCircle2 size={13} className="text-blue-500"/> : <AlertTriangle size={13} className="text-amber-400" title="Conditional"/>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── SECTION 2: CPA GROUPING (conditional — hidden if only 1 group) ── */}
      {showCPA && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-amber-500 rounded-full flex-shrink-0"/>
            <p className="text-sm font-bold text-slate-900">CPA Grouping</p>
            <span className="text-[9px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 ml-1">Step 2</span>
          </div>
          <p className="text-xs text-slate-500 mb-4 max-w-2xl">
            Activities are grouped into Component Project Activities (CPAs) by intervention type.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cpaGroups.map(g => (
              <div key={g.id} className="rounded-xl border p-4" style={{background: g.bg, borderColor: g.border}}>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{background: g.color+"22", color: g.color}}>{g.id}</span>
                <p className="text-sm font-bold text-slate-900 mt-1">{g.label}</p>
                <p className="text-[10px] text-slate-500">{g.description}</p>
                <div className="mt-2 pt-2 border-t border-slate-200/50 text-[10px] space-y-0.5">
                  <div className="flex justify-between"><span className="text-slate-400">Activities</span><span className="font-bold">{g.projects.length}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Reduction</span><span className="font-bold text-emerald-700">{fmtK(g.totalRed)} tCO₂e/yr</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Pathways</span><span className="font-semibold">{g.pathways.join(", ")}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SECTION 3: PoA STRUCTURE (conditional) ──────────────────────── */}
      {showPoA && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1 h-5 bg-purple-500 rounded-full flex-shrink-0"/>
            <p className="text-sm font-bold text-slate-900">Programme of Activities (PoA)</p>
            <span className="text-[9px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 ml-1">Step {showCPA ? 3 : 2}</span>
          </div>
          <p className="text-xs text-slate-500 mb-4 max-w-2xl">
            {poaPath.reasoning || "Project is well-suited for PoA aggregation across multiple sites."}
          </p>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-slate-900 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <p className="text-xs font-bold text-white">PoA Viability</p>
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${
                  poaStatus === "recommended"
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-amber-100 text-amber-700 border-amber-200"
                }`}>{poaStatus === "recommended" ? "Viable" : "Conditional"}</span>
              </div>
              <p className="text-[9px] text-slate-400">Basis: {cpaGroups.length} CPA(s) · {activities.length} activity</p>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-4 text-xs mb-4">
                <div>
                  <p className="text-[9px] text-slate-400 uppercase tracking-wide mb-2">Replication Potential</p>
                  <p className="font-semibold text-slate-700">
                    {poaStatus === "recommended"
                      ? "Well-suited for aggregation across multiple waste operators / sites"
                      : "Single-site at present — replication requires additional feasibility"}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] text-slate-400 uppercase tracking-wide mb-2">Standardisation</p>
                  <p className="font-semibold text-slate-700">
                    Uniform waste processing methodology (DCI-BIOGAS-V1.0) enables standardised MRV
                  </p>
                </div>
              </div>

              {/* Scale potential — derived, with context label and confidence tag */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-3">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Estimated Scale Potential</p>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
                    evPct >= 70 ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
                    evPct >= 40 ? "bg-amber-100 text-amber-700 border-amber-200" :
                    "bg-slate-100 text-slate-500 border-slate-200"
                  }`}>
                    {evPct >= 70 ? "Medium confidence" : evPct >= 40 ? "Low–medium confidence" : "Indicative only"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs mb-3">
                  <div>
                    <p className="text-slate-400 mb-0.5">Site range</p>
                    <p className="font-bold text-slate-900 text-base">{scalePot.siteRange[0]}–{scalePot.siteRange[1]}</p>
                    <p className="text-[9px] text-slate-400">{scalePot.siteLabel}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-0.5">Aggregated reduction</p>
                    <p className="font-bold text-emerald-700 text-base">{scalePot.redRange[0]}K–{scalePot.redRange[1]}K</p>
                    <p className="text-[9px] text-slate-400">tCO₂e/year</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-0.5">Positioning</p>
                    <p className="font-semibold text-slate-700 text-[10px] leading-tight">
                      Suitable for PoA under VCM or Article 6
                    </p>
                  </div>
                </div>
                <p className="text-[9px] text-slate-400 italic border-t border-slate-200 pt-2">
                  Indicative scale potential based on replication of similar waste-to-biogas facilities.
                  Assumes {scalePot.siteRange[0]}–{scalePot.siteRange[1]} comparable sites with similar activity data.
                  Requires independent feasibility assessment before PoA registration.
                </p>
              </div>

              {poaPath.next_action && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-[10px] text-slate-600">
                  <span className="font-bold text-slate-500 uppercase">Next: </span>{poaPath.next_action}
                </div>
              )}
              <div className="mt-3 flex items-start gap-1.5">
                <Info size={10} className="text-slate-400 flex-shrink-0 mt-0.5"/>
                <p className="text-[9px] text-slate-400 italic">
                  PoA structure is a preparation activity. Not approval or issuance.
                  Requires CDM/Verra PoA administrator engagement.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SECTION 4: PROJECT AGGREGATION SUMMARY (mandatory) ─────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 bg-emerald-500 rounded-full flex-shrink-0"/>
          <p className="text-sm font-bold text-slate-900">Project Aggregation Summary</p>
          <span className="text-[9px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 ml-1">
            Step {showCPA && showPoA ? 4 : showCPA || showPoA ? 3 : 2}
          </span>
        </div>
        <p className="text-xs text-slate-500 mb-4 max-w-2xl">
          Portfolio of reduction and financial outcomes from all structuring activities.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Aggregated totals */}
          <SectionCard>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Aggregated Totals</p>
            <div className="space-y-2 text-xs">
              {[
                ["Total Reduction", `${fmtK(aggregation.totalRed)} tCO₂e/yr`, "font-bold text-emerald-700"],
                ["Total CAPEX",      aggregation.totalCap > 0 ? fmtV(aggregation.totalCap) : "—", "font-bold text-amber-600"],
                ["Annual Savings",   aggregation.totalSav > 0 ? `${fmtV(aggregation.totalSav)}/yr` : "—", "font-bold text-teal-700"],
                ["Blended Payback",  aggregation.blended, "font-bold text-slate-700"],
              ].map(([l,v,c]) => (
                <div key={l} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <span className="text-slate-500">{l}</span>
                  <span className={c}>{v}</span>
                </div>
              ))}
            </div>

            {/* Pathway representation — qualitative, no 0% */}
            <div className="mt-3 pt-3 border-t border-slate-100">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-2">Pathway Representation</p>
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0 mt-1"/>
                  <div>
                    <p className="font-semibold text-slate-700">VCM — {vcmStatusLabel}</p>
                    <p className="text-[10px] text-slate-400">{fmtK(aggregation.totalRed)} tCO₂e/yr eligible</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0 mt-1"/>
                  <div>
                    <p className="font-semibold text-slate-700">Article 6 — {art6StatusLabel.split(" — ")[0]}</p>
                    <p className="text-[10px] text-slate-400">
                      {art6Path.status !== "not_eligible"
                        ? "Requires LoA and corresponding adjustment before any ITMO can be issued"
                        : "Not eligible for this project type"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Readiness + bankability */}
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Readiness Indicator</p>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${issReady.cls}`}>
                  {issReady.label}
                </span>
              </div>
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Bankability Signal</p>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${bankability.cls}`}>
                  {bankability.label}
                </span>
              </div>
            </div>
          </SectionCard>

          {/* Issuance readiness + next steps */}
          <div className="space-y-3">
            <div className={`border rounded-xl p-5 ${issReady.cls}`}>
              <p className="text-[9px] font-bold uppercase tracking-widest mb-2">Blended Issuance Readiness</p>
              <p className="text-xl font-bold">{issReady.label}</p>
              <p className="text-[10px] mt-1 opacity-70">Based on pipeline stage of {activities.length} activity</p>
              <div className="mt-3 pt-3 border-t border-current/20 space-y-1.5 text-[10px]">
                {[
                  ["Evidence coverage",  `${evPct}%`],
                  ["VCM eligible",       `${fmtK(aggregation.vcmRed)} tCO₂e/yr`],
                  ["Art.6 eligible",     `${fmtK(aggregation.art6Red)} tCO₂e/yr`],
                  ["Methodology",        "DCI-BIOGAS-V1.0"],
                ].map(([l,v]) => (
                  <div key={l} className="flex justify-between">
                    <span className="opacity-70">{l}</span>
                    <span className="font-bold">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-3">Next Steps</p>
              <div className="space-y-2">
                {[
                  { done: evPct >= 50,                           label: "Evidence coverage ≥50%" },
                  { done: vcmPath.status === "recommended",      label: "VCM pathway confirmed" },
                  { done: false,                                 label: "Independent technical review" },
                  { done: false,                                 label: "VVB engagement for validation" },
                  { done: art6Path.requirements_met || false,    label: "Art.6 host country authorization" },
                ].map(({done,label}) => (
                  <div key={label} className="flex items-center gap-2 text-xs">
                    {done
                      ? <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0"/>
                      : <div className="w-3 h-3 rounded-full border-2 border-slate-300 flex-shrink-0"/>}
                    <span className={done ? "text-slate-700" : "text-slate-400"}>{label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <ProjectLoAButton projectId={id} />
              </div>
            </div>
          </div>
        </div>

        {/* Indicative carbon value */}
        {totalRed > 0 && (
          <div className="mt-4 bg-white border border-slate-200 rounded-xl p-5">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Indicative Carbon Value Scenarios</p>
            <div className="grid grid-cols-2 gap-5">
              {[
                { label: "VCM", reduction: aggregation.vcmRed, scenarios: [[5,"Low"],[10,"Base"],[20,"High"]] },
                { label: "Article 6", reduction: aggregation.art6Red || aggregation.vcmRed, scenarios: [[10,"Low"],[20,"Base"],[40,"High"]] },
              ].map(({label, reduction: r, scenarios}) => (
                <div key={label}>
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-2">{label} · Based on {fmtK(r)} tCO₂e/yr</p>
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-slate-100">
                      <th className="text-left text-[9px] text-slate-400 pb-1.5 pr-3 font-semibold uppercase">Scenario</th>
                      <th className="text-right text-[9px] text-slate-400 pb-1.5 pr-3 font-semibold uppercase">$/tCO₂e</th>
                      <th className="text-right text-[9px] text-slate-400 pb-1.5 font-semibold uppercase">Indicative/yr</th>
                    </tr></thead>
                    <tbody>
                      {scenarios.map(([price,scenLabel]) => (
                        <tr key={scenLabel} className="border-b border-slate-50 last:border-0">
                          <td className="py-1.5 pr-3 text-slate-500">{scenLabel}</td>
                          <td className="py-1.5 pr-3 text-right text-slate-500">${price}</td>
                          <td className="py-1.5 text-right font-semibold text-slate-600 italic">
                            {fmtV(Math.round(r * price / 10000) * 10000)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-start gap-1.5">
              <AlertTriangle size={10} className="text-amber-400 flex-shrink-0 mt-0.5"/>
              <p className="text-[9px] text-slate-400 italic">
                Indicative only — subject to methodology validation, VVB verification, credit issuance, and market conditions.
                Not a carbon eligibility determination, methodology approval, or Article 6 authorization.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── SECTION 5: NEXT STEPS TO MONETISATION (mandatory) ─────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 bg-slate-800 rounded-full flex-shrink-0"/>
          <p className="text-sm font-bold text-slate-900">Next Steps to Monetisation</p>
        </div>
        <SectionCard>
          <p className="text-[10px] text-slate-400 mb-4">
            Dynamic checklist based on current issuance readiness ({issReady.label}),
            pathway status, and evidence coverage ({evPct}%).
          </p>
          <div className="space-y-2.5">
            {nextSteps.map(({text, done, priority}, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  {done
                    ? <CheckCircle2 size={14} className="text-emerald-500"/>
                    : <div className={`w-3.5 h-3.5 rounded-full border-2 ${
                        priority === "high" ? "border-red-400" :
                        priority === "medium" ? "border-amber-400" : "border-slate-300"
                      }`}/>}
                </div>
                <div className="flex-1">
                  <span className={`text-xs ${done ? "text-slate-700 font-medium" : "text-slate-500"}`}>{text}</span>
                  {!done && (
                    <span className={`ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      priority === "high" ? "bg-red-50 text-red-600" :
                      priority === "medium" ? "bg-amber-50 text-amber-600" : "bg-slate-50 text-slate-400"
                    }`}>{priority}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-[9px] text-slate-400">
              {nextSteps.filter(s=>s.done).length}/{nextSteps.length} steps complete
            </p>
            <ProjectLoAButton projectId={id} />
          </div>
        </SectionCard>
      </div>

      {/* ── DISCLAIMER ────────────────────────────────────────────────────── */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
        <Shield size={13} className="text-amber-500 flex-shrink-0 mt-0.5"/>
        <p className="text-xs text-amber-700">
          <strong>Carbon structuring is a preparation activity only.</strong>{" "}
          DCI-BIOGAS-V1.0 pathway assessments are indicative. Not a carbon credit, registered emission reduction,
          or Article 6 authorization. Requires independent VVB validation before any credits can be issued.
        </p>
      </div>
    </div>
  );
}
